import { SUPPORTED_TOKEN_CONTRACTS, SupportedTokenName } from "@mezo/sdk"

import { SDK_NETWORK } from "#/shared/constants"
import { TokenOperation } from "#/shared/types"
import { getLockData } from "#/shared/utils"

import createDappAsyncThunk from "../asyncThunk"
import { setDepositStatus } from "../slices/deposit"
import {
  setActionStatus,
  setActionType,
  setWithdrawalStep,
} from "../slices/deposit-action"
import { setIsProcessingTransaction } from "../slices/wallet"

import { fetchMats } from "./mats"
import { fetchTokenAllowances, updateBalances } from "./portal"
import { fetchAccountReferralCodeData } from "./referrals"

export const approveToken = createDappAsyncThunk(
  "transactions/approveToken",
  async ({ amount, token }: TokenOperation, { dispatch, extra: { sdk } }) => {
    const { address, abi } = SUPPORTED_TOKEN_CONTRACTS[token]
    const tokenForDeposit = { address: address[SDK_NETWORK], abi }

    dispatch(setIsProcessingTransaction(true))

    const approval = await sdk.mezo.chain?.depositModule.approveForDeposit(
      tokenForDeposit,
      amount,
    )

    dispatch(setIsProcessingTransaction(false))

    if (approval) {
      dispatch(fetchTokenAllowances())
    }

    return !!approval
  },
)

export const depositToken = createDappAsyncThunk(
  "transactions/depositToken",
  async (
    {
      amount,
      token,
      lockupTime,
    }: TokenOperation & {
      lockupTime?: number
    },
    { getState, dispatch, extra: { sdk } },
  ) => {
    const { isConnected, balances } = getState().wallet
    if (!isConnected) return false

    const { balance: tokenBalance } = balances[token]
    const { address, abi } = SUPPORTED_TOKEN_CONTRACTS[token]
    const tokenForDeposit = { address: address[SDK_NETWORK], abi }

    if (amount > tokenBalance) return false

    const { lockStart, lockEnd } = getLockData(lockupTime ?? 0)

    dispatch(setIsProcessingTransaction(true))
    dispatch(setDepositStatus("pending"))

    const deposit = await sdk.mezo.chain?.depositModule.deposit(
      tokenForDeposit,
      amount,
      { lockStart, lockEnd, lockupTime },
    )

    await dispatch(updateBalances({ fetchOnChainData: true }))
    await dispatch(fetchAccountReferralCodeData())
    await dispatch(fetchMats())

    dispatch(setIsProcessingTransaction(false))

    if (deposit) {
      dispatch(setDepositStatus("completed"))
    } else {
      dispatch(setDepositStatus("idle"))
    }

    return !!deposit
  },
)

export const withdrawToken = createDappAsyncThunk(
  "transactions/withdrawToken",
  async (
    {
      isBitcoinDeposit,
      id,
      amount,
      token,
      depositId,
    }: TokenOperation & {
      isBitcoinDeposit: boolean
      id: string
      depositId: bigint
    },
    { getState, dispatch, extra: { sdk } },
  ) => {
    const { isConnected } = getState().wallet
    const { deposits } = getState().portal

    if (!isConnected || !deposits) return false

    const tokenDepositBalance = deposits.find(
      (deposit) =>
        deposit.depositId && BigInt(deposit.depositId!) === depositId,
    )?.amount

    const { address, abi } = SUPPORTED_TOKEN_CONTRACTS[token]
    const tokenForWithdrawal = { address: address[SDK_NETWORK], abi }

    if (!tokenDepositBalance || amount > BigInt(tokenDepositBalance))
      return false

    dispatch(setIsProcessingTransaction(true))
    dispatch(setWithdrawalStep("withdraw"))
    dispatch(setActionStatus("in-progress"))

    const result = await sdk.mezo.chain?.withdrawalModule.withdraw(
      id,
      tokenForWithdrawal,
      depositId,
      amount,
      isBitcoinDeposit,
    )

    dispatch(setIsProcessingTransaction(false))

    if (!result) {
      dispatch(setActionStatus("error"))
      return false
    }

    await dispatch(updateBalances({ fetchOnChainData: true }))
    dispatch(setActionStatus("idle"))

    return !!result
  },
)

export const withdrawBitcoin = createDappAsyncThunk(
  "transactions/withdrawBitcoin",
  async (
    {
      id,
      amount,
      depositId,
      bitcoinRedeemerAddress,
      shouldOnlyRequestRedemption = false,
    }: {
      id: string
      depositId: bigint
      amount: bigint
      bitcoinRedeemerAddress?: string
      shouldOnlyRequestRedemption?: boolean
    },
    { getState, dispatch, extra: { sdk } },
  ) => {
    const { isConnected } = getState().wallet

    if (!isConnected) return false

    dispatch(setIsProcessingTransaction(true))

    const withdrawObject = {
      withdrawnDepositUuid: id,
      depositId: BigInt(depositId),
      amount,
      bitcoinRedeemerAddress: bitcoinRedeemerAddress ?? null,
    }

    if (!shouldOnlyRequestRedemption) {
      dispatch(setActionStatus("in-progress"))
      dispatch(setWithdrawalStep("retrieve"))

      const withdrawToBtcResult =
        await sdk.mezo.chain?.tbtcModule.withdrawBitcoinDepositToTBTC(
          withdrawObject,
        )

      dispatch(setActionStatus("processing"))

      if (!withdrawToBtcResult) {
        dispatch(setIsProcessingTransaction(false))
        dispatch(setActionStatus("error"))
        return false
      }
    }

    dispatch(setWithdrawalStep("withdraw"))
    dispatch(setActionStatus("in-progress"))

    const redemptionResult =
      await sdk.mezo.chain?.tbtcModule.requestBitcoinRedemption(withdrawObject)

    dispatch(setActionStatus("processing"))
    dispatch(setIsProcessingTransaction(false))

    if (!redemptionResult) {
      dispatch(setActionStatus("error"))
      dispatch(setActionType("redemption"))
      return false
    }

    await dispatch(updateBalances({ fetchOnChainData: true }))
    dispatch(setActionStatus("idle"))

    return true
  },
)

export const relockDeposit = createDappAsyncThunk(
  "transactions/relockDeposit",
  async (
    {
      id,
      token,
      depositId,
      lockupTime,
    }: {
      id: string
      depositId: bigint
      token: SupportedTokenName
      lockupTime: number
    },
    { getState, dispatch, extra: { sdk } },
  ): Promise<boolean> => {
    const { isConnected } = getState().wallet

    if (!isConnected) return false

    dispatch(setActionStatus("in-progress"))

    const { address, abi } =
      token === "BTC"
        ? SUPPORTED_TOKEN_CONTRACTS.tBTC
        : SUPPORTED_TOKEN_CONTRACTS[token]

    const tokenForRelock = { address: address[SDK_NETWORK], abi }

    const { lockEnd } = getLockData(lockupTime ?? 0)

    dispatch(setIsProcessingTransaction(true))
    dispatch(setActionStatus("processing"))

    const relock = await sdk.mezo.chain?.depositModule.relockDeposit(
      id,
      tokenForRelock,
      depositId,
      { lockEnd, lockupTime },
    )

    dispatch(setIsProcessingTransaction(false))
    dispatch(setActionStatus(relock ? "completed" : "error"))

    if (relock) {
      await dispatch(updateBalances({ fetchOnChainData: false }))
    }

    return !!relock
  },
)

export const migrateDepositToTbtc = createDappAsyncThunk(
  "transactions/migrateDeposit",
  async (
    {
      depositUuid,
      depositId,
      token,
    }: {
      depositUuid: string
      depositId: number
      token: SupportedTokenName
    },
    { getState, dispatch, extra: { sdk } },
  ): Promise<boolean> => {
    const { isConnected } = getState().wallet

    if (!isConnected) return false

    const { address, abi } = SUPPORTED_TOKEN_CONTRACTS[token]
    const tokenForDeposit = { address: address[SDK_NETWORK], abi }

    dispatch(setIsProcessingTransaction(true))

    const depositMigrationResult =
      await sdk.mezo.chain?.depositMigrationModule.requestTbtcMigration(
        depositUuid,
        depositId,
        tokenForDeposit,
      )

    dispatch(setIsProcessingTransaction(false))

    if (!depositMigrationResult) {
      return false
    }

    await dispatch(updateBalances({ fetchOnChainData: false }))

    return true
  },
)
